<!--
  AddressCombobox — the booking form's address input.

  One input, one dropdown, two sources: addresses this company has used before
  (favourites pinned to the top) and live Google predictions.

  It replaces MapAutocompleteInput, which attached
  `google.maps.places.Autocomplete` to a bare <input> and let Google render its
  own list — a list we cannot add anything to. That is why a customer's own
  depot could only ever have appeared in a second, competing dropdown, which
  Google's overrode the moment they typed a letter.

  Predictions arrive as plain data from utils/placesAutocomplete, so this
  component neither knows nor cares whether the new or the legacy Places API
  answered.

  It emits a fully resolved location — name, formatted_address, lat, lng,
  place_id — in one shape regardless of source. A frequently-used address is
  already resolved, so choosing one costs no Google call at all.
-->
<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import {
  createPlacesSession,
  splitPlaceName,
  type Prediction,
  type ResolvedPlace,
} from '../../utils/placesAutocomplete'

export interface SuggestedAddress {
  identity_key: string
  place_id: string | null
  name: string
  formatted_address: string | null
  lat: number
  lng: number
  uses: number
  pickup_share: number
  is_favourite: boolean
}

const props = withDefaults(defineProps<{
  modelValue: string
  suggestions: SuggestedAddress[]
  /** 0 = pickup. Drives the placeholder and the ranking bias. */
  stopIndex: number
  placeholder?: string
  inputClass?: string
}>(), { placeholder: '', inputClass: '' })

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void
  (e: 'resolved', place: ResolvedPlace): void
  (e: 'favourite', address: SuggestedAddress): void
  (e: 'unfavourite', address: SuggestedAddress): void
}>()

type Row =
  | { kind: 'saved'; key: string; primary: string; secondary: string; address: SuggestedAddress }
  | { kind: 'prediction'; key: string; primary: string; secondary: string; prediction: Prediction }

const session = createPlacesSession()
const inputEl = ref<HTMLInputElement | null>(null)
const open = ref(false)
// The dropdown is TELEPORTED to <body> and positioned from the input's
// bounding rect, rather than being absolutely positioned next to it.
//
// It has to be. The stop rows sit inside `<el-scrollbar max-height="220px">`
// and the booking panel itself is `overflow-y:auto; overflow-x:hidden`, so an
// in-flow absolute panel is clipped on both axes — the list would be cut off
// after two rows, and completely invisible for the later stops. Scoped styles
// still apply because Vue stamps the data-v attribute on teleported nodes.
const panelStyle = ref<Record<string, string>>({})
const active = ref(-1)
const predictions = ref<Prediction[]>([])
const loading = ref(false)
const resolving = ref(false)
const listId = `addr-list-${Math.random().toString(36).slice(2, 9)}`

// ── Ranking ────────────────────────────────────────────────────────────
// Favourites first, then frequency weighted by whether this address is
// normally used at THIS kind of stop. A warehouse should lead the pickup list
// without vanishing from the dropoff list, so the bias scales the score rather
// than filtering.
function score(a: SuggestedAddress): number {
  const roleFit = props.stopIndex === 0 ? a.pickup_share : 1 - a.pickup_share
  return a.uses * (0.5 + roleFit)
}

const query = computed(() => (props.modelValue || '').trim())

const savedRows = computed<Row[]>(() => {
  const q = query.value.toLowerCase()
  return [...props.suggestions]
    // While typing, narrow the saved list the same way the eye would — the
    // whole point of the merged dropdown is that these stay visible instead of
    // being replaced by Google's results.
    .filter((a) => !q || a.name.toLowerCase().includes(q))
    .sort((x, y) => {
      if (x.is_favourite !== y.is_favourite) return x.is_favourite ? -1 : 1
      return score(y) - score(x)
    })
    .slice(0, 6)
    .map((a) => {
      const { primary, secondary } = splitPlaceName(a.name)
      return {
        kind: 'saved' as const,
        key: `s:${a.identity_key}`,
        primary,
        secondary: secondary || a.formatted_address || '',
        address: a,
      }
    })
})

const predictionRows = computed<Row[]>(() => {
  // Never offer a Google result for something already in the saved list —
  // the same place twice, one of them without the use count, reads as a bug.
  const seen = new Set(
    props.suggestions.map((a) => (a.place_id || '').trim()).filter(Boolean),
  )
  return predictions.value
    .filter((p) => !seen.has(p.place_id))
    .map((p) => ({
      kind: 'prediction' as const,
      key: `p:${p.place_id}`,
      primary: p.primary,
      secondary: p.secondary,
      prediction: p,
    }))
})

const rows = computed<Row[]>(() => [...savedRows.value, ...predictionRows.value])
const showAttribution = computed(() => predictionRows.value.length > 0)

// Say so when a search genuinely found nothing, rather than rendering no panel
// at all. Showing nothing made a real outage indistinguishable from "no such
// place": when Places API (New) was returning 403 for every query, the field
// just sat there silently and looked broken with no clue why.
const showEmpty = computed(
  () => query.value.length >= 2 && !loading.value && rows.value.length === 0,
)

// ── Prediction fetching ────────────────────────────────────────────────
let debounce: number | null = null
let seq = 0

function schedulePredictions(text: string) {
  if (debounce !== null) window.clearTimeout(debounce)
  const q = text.trim()
  if (q.length < 2) {
    predictions.value = []
    loading.value = false
    return
  }
  loading.value = true
  debounce = window.setTimeout(async () => {
    const mine = ++seq
    const out = await session.predict(q)
    // Drop a slow response that a newer keystroke has already superseded,
    // otherwise the list flickers back to stale results.
    if (mine !== seq) return
    predictions.value = out
    loading.value = false
  }, 250)
}

function onInput(ev: Event) {
  const value = (ev.target as HTMLInputElement).value
  emit('update:modelValue', value)
  open.value = true
  active.value = -1
  schedulePredictions(value)
}

function onFocus() {
  open.value = true
  active.value = -1
  // Offer the saved list straight away on an empty field — that is the case
  // this whole feature exists for.
  if (query.value.length >= 2) schedulePredictions(query.value)
}

function close() {
  open.value = false
  active.value = -1
}

const PANEL_MAX_H = 300

function reposition() {
  const el = inputEl.value
  if (!el) return
  const r = el.getBoundingClientRect()
  const below = window.innerHeight - r.bottom
  // Flip above when there isn't room below — the booking panel sits low on
  // short viewports, where a downward list would run off the screen.
  const flip = below < 180 && r.top > below
  panelStyle.value = flip
    ? { left: `${r.left}px`, width: `${r.width}px`, bottom: `${window.innerHeight - r.top + 4}px`,
        maxHeight: `${Math.min(PANEL_MAX_H, r.top - 12)}px` }
    : { left: `${r.left}px`, width: `${r.width}px`, top: `${r.bottom + 4}px`,
        maxHeight: `${Math.min(PANEL_MAX_H, below - 12)}px` }
}

// Capture phase: the input scrolls inside el-scrollbar, whose scroll events do
// not bubble to window. Without capture the panel detaches from the field.
function onViewportChange() {
  if (open.value) reposition()
}

watch(open, (isOpen) => {
  if (isOpen) {
    reposition()
    window.addEventListener('scroll', onViewportChange, true)
    window.addEventListener('resize', onViewportChange)
  } else {
    window.removeEventListener('scroll', onViewportChange, true)
    window.removeEventListener('resize', onViewportChange)
  }
})

// ── Choosing ───────────────────────────────────────────────────────────
async function choose(row: Row) {
  if (row.kind === 'saved') {
    const a = row.address
    emit('resolved', {
      name: a.name,
      formatted_address: a.formatted_address || '',
      lat: a.lat,
      lng: a.lng,
      place_id: a.place_id || '',
    })
    close()
    return
  }
  resolving.value = true
  const resolved = await session.resolve(row.prediction.place_id)
  resolving.value = false
  if (resolved) {
    emit('resolved', resolved)
    close()
  }
  // If Google could not resolve it, the dropdown stays open with the text
  // intact so the customer can pick something else rather than being left with
  // a silently empty field.
}

function onKeydown(ev: KeyboardEvent) {
  if (ev.key === 'Escape') {
    if (open.value) { ev.stopPropagation(); close() }
    return
  }
  if (!open.value || !rows.value.length) {
    if (ev.key === 'ArrowDown') { open.value = true; active.value = 0; ev.preventDefault() }
    return
  }
  if (ev.key === 'ArrowDown') {
    ev.preventDefault()
    active.value = (active.value + 1) % rows.value.length
  } else if (ev.key === 'ArrowUp') {
    ev.preventDefault()
    active.value = active.value <= 0 ? rows.value.length - 1 : active.value - 1
  } else if (ev.key === 'Enter') {
    // Only intercept Enter when something is actually highlighted, so Enter on
    // a plain typed value still submits the form as before.
    if (active.value >= 0) { ev.preventDefault(); choose(rows.value[active.value]) }
  }
}

// Blur closes, but not before a click on a row has registered.
let blurTimer: number | null = null
function onBlur() {
  blurTimer = window.setTimeout(close, 120)
}
function cancelBlur() {
  if (blurTimer !== null) { window.clearTimeout(blurTimer); blurTimer = null }
}

function togglePin(a: SuggestedAddress, ev: Event) {
  ev.preventDefault()
  ev.stopPropagation()
  cancelBlur()
  emit(a.is_favourite ? 'unfavourite' : 'favourite', a)
  nextTick(() => inputEl.value?.focus())
}

watch(() => props.modelValue, (v) => {
  if (inputEl.value && inputEl.value.value !== v) inputEl.value.value = v ?? ''
})

onBeforeUnmount(() => {
  if (debounce !== null) window.clearTimeout(debounce)
  if (blurTimer !== null) window.clearTimeout(blurTimer)
  window.removeEventListener('scroll', onViewportChange, true)
  window.removeEventListener('resize', onViewportChange)
  session.dispose()
})
</script>

<template>
  <div class="addrcb">
    <input
      ref="inputEl"
      :value="modelValue"
      :placeholder="placeholder"
      :class="inputClass"
      role="combobox"
      aria-autocomplete="list"
      :aria-expanded="open"
      :aria-controls="listId"
      :aria-activedescendant="active >= 0 && rows[active] ? `${listId}-${active}` : undefined"
      autocomplete="off"
      @input="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown="onKeydown"
    />

    <Teleport to="body">
    <div
      v-if="open && (rows.length || loading || showEmpty)"
      class="addrcb__panel"
      :style="panelStyle"
      @mousedown="cancelBlur"
    >
      <ul :id="listId" class="addrcb__list" role="listbox">
        <li
          v-if="savedRows.length"
          class="addrcb__group"
          role="presentation"
        >{{ stopIndex === 0 ? 'Your pickup addresses' : 'Your addresses' }}</li>

        <li
          v-for="(row, i) in rows"
          :id="`${listId}-${i}`"
          :key="row.key"
          class="addrcb__row"
          :class="{ 'is-active': i === active, 'is-saved': row.kind === 'saved' }"
          role="option"
          :aria-selected="i === active"
          @mouseenter="active = i"
          @mousedown.prevent="choose(row)"
        >
          <span class="addrcb__icon" aria-hidden="true">
            <template v-if="row.kind === 'saved' && row.address.is_favourite">★</template>
            <template v-else-if="row.kind === 'saved'">◷</template>
            <template v-else>⌕</template>
          </span>

          <span class="addrcb__text">
            <span class="addrcb__primary">{{ row.primary }}</span>
            <span v-if="row.secondary" class="addrcb__secondary">{{ row.secondary }}</span>
          </span>

          <span v-if="row.kind === 'saved' && row.address.uses > 1" class="addrcb__uses">
            {{ row.address.uses }}&times;
          </span>

          <button
            v-if="row.kind === 'saved'"
            type="button"
            class="addrcb__pin"
            :class="{ 'is-on': row.address.is_favourite }"
            :title="row.address.is_favourite ? 'Remove from favourites' : 'Save as a favourite'"
            :aria-label="row.address.is_favourite ? 'Remove from favourites' : 'Save as a favourite'"
            @mousedown.stop.prevent="togglePin(row.address, $event)"
          >★</button>
        </li>

        <li v-if="loading && !rows.length" class="addrcb__hint" role="presentation">Searching…</li>
        <li v-else-if="showEmpty" class="addrcb__hint" role="presentation">
          No matching addresses
        </li>
        <li v-if="resolving" class="addrcb__hint" role="presentation">Getting the location…</li>
      </ul>

      <!-- Required by the Places terms whenever predictions are shown outside
           a Google map. -->
      <div v-if="showAttribution" class="addrcb__attr">Powered by Google</div>
    </div>
    </Teleport>
  </div>
</template>

<style scoped>
.addrcb { position: relative; width: 100%; }

/* Teleported to body, so position: fixed with coordinates from the input.
   left / width / top|bottom / max-height are all set inline by reposition(). */
.addrcb__panel {
  position: fixed;
  /* Above the booking panel (z-index 10-ish) and Element Plus poppers (2000+),
     below nothing else on this screen. */
  z-index: 4000;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
  box-shadow: 0 10px 28px rgba(18, 40, 74, 0.16);
  overflow: hidden;
}

.addrcb__list {
  margin: 0;
  padding: 4px;
  list-style: none;
  /* The panel's own max-height is the budget; the list takes what's left so
     the attribution footer stays visible. */
  overflow-y: auto;
  min-height: 0;
}

.addrcb__group {
  padding: 6px 8px 4px;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
}

.addrcb__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 8px;
  border-radius: 7px;
  cursor: pointer;
  font-family: 'DM Sans', system-ui, sans-serif;
}
.addrcb__row.is-active { background: #f1f5f9; }

.addrcb__icon {
  width: 16px;
  flex-shrink: 0;
  text-align: center;
  font-size: 12px;
  color: #94a3b8;
}
.addrcb__row.is-saved .addrcb__icon { color: #12284a; }

.addrcb__text { display: flex; flex-direction: column; min-width: 0; flex: 1 1 auto; }
.addrcb__primary {
  font-size: 13px;
  font-weight: 500;
  color: #12284a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.addrcb__secondary {
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.addrcb__uses {
  flex-shrink: 0;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  color: #94a3b8;
}

.addrcb__pin {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
}
.addrcb__pin:hover { background: #e2e8f0; color: #12284a; }
.addrcb__pin.is-on { color: #f2b21c; }

.addrcb__hint {
  padding: 8px;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 12px;
  color: #94a3b8;
}

.addrcb__attr {
  padding: 5px 8px;
  border-top: 1px solid #e2e8f0;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 10px;
  color: #94a3b8;
  text-align: right;
}
</style>
